import React from 'react';
import { Alert, Card, Col, Progress, Row, Statistic, Tag, Typography } from 'antd';
import type { ProjectOperatingInsights } from '../../services/statisticsService';

const { Text, Title } = Typography;

interface OperatingInsightsPanelProps {
  insights: ProjectOperatingInsights | null;
  projectId: string;
  isMobile?: boolean;
}

const CONSTRUCTION_TYPE_CONFIG = [
  { type: '土建' as const, label: '土建成本统计', color: '#547590', bg: 'rgba(84, 117, 144, 0.08)', border: 'rgba(84, 117, 144, 0.2)', tagColor: 'blue' },
  { type: '园林' as const, label: '园林成本统计', color: '#5ad1b2', bg: 'rgba(90, 209, 178, 0.08)', border: 'rgba(90, 209, 178, 0.2)', tagColor: 'cyan' },
  { type: '外墙' as const, label: '外墙成本统计', color: '#b99258', bg: 'rgba(185, 146, 88, 0.08)', border: 'rgba(185, 146, 88, 0.2)', tagColor: 'gold' },
  { type: '其它' as const, label: '其它成本统计', color: '#9384ff', bg: 'rgba(147, 132, 255, 0.08)', border: 'rgba(147, 132, 255, 0.2)', tagColor: 'purple' },
] as const;

const formatMoney = (value?: number) =>
  `¥${Number(value || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const metricLabelStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#667986',
};

const metricValueStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#10171d',
  fontWeight: 600,
  lineHeight: 1.6,
};

interface MetricRowProps {
  label: string;
  value: number;
  color?: string;
  ratio?: number;
  progressColor?: string;
  showProgress?: boolean;
}

interface MetricItem extends MetricRowProps {
  key: string;
}

const MetricRow: React.FC<MetricRowProps> = ({
  label,
  value,
  color,
  ratio = 0,
  progressColor,
  showProgress = false,
}) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
      <Text style={metricLabelStyle}>{label}</Text>
      <div style={{ ...metricValueStyle, color: color || '#10171d' }}>{formatMoney(value)}</div>
    </div>
    {showProgress ? (
      <Progress
        percent={Number(ratio || 0)}
        size="small"
        strokeColor={progressColor}
        trailColor="rgba(16,27,35,0.08)"
        style={{ marginTop: 6, marginBottom: 0 }}
      />
    ) : null}
  </div>
);

const SECTION_VARIANTS = {
  income: {
    titleColor: '#2f6f89',
    background: 'linear-gradient(180deg, rgba(84, 117, 144, 0.10) 0%, rgba(84, 117, 144, 0.04) 100%)',
    border: '1px solid rgba(84, 117, 144, 0.14)',
  },
  cost: {
    titleColor: '#8a5c24',
    background: 'linear-gradient(180deg, rgba(185, 146, 88, 0.10) 0%, rgba(185, 146, 88, 0.04) 100%)',
    border: '1px solid rgba(185, 146, 88, 0.16)',
  },
} as const;

const MetricSection: React.FC<{
  title: string;
  items: MetricItem[];
  progressColor: string;
  variant: keyof typeof SECTION_VARIANTS;
}> = ({ title, items, progressColor, variant }) => {
  if (items.length === 0) {
    return null;
  }

  const sectionStyle = SECTION_VARIANTS[variant];

  return (
    <div style={{ marginTop: 14 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: sectionStyle.titleColor,
          letterSpacing: 0.3,
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      <div
        style={{
          padding: '10px 12px',
          borderRadius: 12,
          background: sectionStyle.background,
          border: sectionStyle.border,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25)',
        }}
      >
        {items.map((item) => (
          <MetricRow
            key={item.key}
            label={item.label}
            value={item.value}
            color={item.color}
            ratio={item.ratio}
            progressColor={progressColor}
            showProgress={item.showProgress}
          />
        ))}
      </div>
    </div>
  );
};

type ConstructionTypeStat = NonNullable<ProjectOperatingInsights['constructionTypeStats']>[number];

const OperatingInsightsPanel: React.FC<OperatingInsightsPanelProps> = ({
  insights,
  isMobile = false,
}) => {
  if (!insights) return null;

  const { dashboard, businessChain, constructionTypeStats } = insights;
  const budgetAlertType = dashboard.budgetStatus.level === 'healthy'
    ? 'success'
    : dashboard.budgetStatus.level === 'warning'
      ? 'warning'
      : 'error';

  const statsMap = new Map((constructionTypeStats || []).map((s) => [s.type, s]));
  const totalAllCost = (constructionTypeStats || []).reduce((sum, s) => sum + s.totalCost, 0);

  return (
    <div
      style={{
        marginBottom: isMobile ? 16 : 24,
        padding: isMobile ? 14 : 18,
        borderRadius: 20,
        background: 'rgba(210, 218, 224, 0.5)',
        border: '1px solid rgba(16, 27, 35, 0.08)'
      }}
    >
      <Title level={5} style={{ marginBottom: 16, fontSize: isMobile ? 14 : 16, color: '#10171d' }}>
        经营分析与预警
      </Title>

      <Row gutter={[isMobile ? 10 : 16, isMobile ? 10 : 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" style={{ borderRadius: 16, background: 'rgba(84, 117, 144, 0.08)', border: '1px solid rgba(16, 27, 35, 0.06)' }}>
            <Statistic title="预计利润" value={dashboard.operatingSummary.estimatedProfit} precision={2} prefix="¥" valueStyle={{ color: dashboard.operatingSummary.estimatedProfit >= 0 ? '#547590' : '#cf1322', fontSize: isMobile ? 18 : 22 }} />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" style={{ borderRadius: 16, background: 'rgba(90, 209, 178, 0.08)', border: '1px solid rgba(16, 27, 35, 0.06)' }}>
            <Statistic title="预算剩余" value={dashboard.operatingSummary.budgetRemaining} precision={2} prefix="¥" valueStyle={{ color: '#5ad1b2', fontSize: isMobile ? 18 : 22 }} />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" style={{ borderRadius: 16, background: 'rgba(185, 146, 88, 0.08)', border: '1px solid rgba(16, 27, 35, 0.06)' }}>
            <Statistic title="高风险项" value={businessChain.highRiskCount} suffix="项" valueStyle={{ color: '#b99258', fontSize: isMobile ? 18 : 22 }} />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" style={{ borderRadius: 16, background: 'rgba(147, 132, 255, 0.08)', border: '1px solid rgba(16, 27, 35, 0.06)' }}>
            <Statistic title="已结算整改" value={businessChain.settledRectificationCount} suffix="项" valueStyle={{ color: '#9384ff', fontSize: isMobile ? 18 : 22 }} />
          </Card>
        </Col>
      </Row>

      <Alert
        type={budgetAlertType}
        showIcon
        message={dashboard.budgetStatus.message}
        style={{ marginBottom: 16, borderRadius: 14 }}
      />

      <Row gutter={[isMobile ? 10 : 16, isMobile ? 10 : 16]}>
        {CONSTRUCTION_TYPE_CONFIG.map((cfg) => {
          const stat: ConstructionTypeStat = statsMap.get(cfg.type) || {
            type: cfg.type,
            contractRevenue: 0,
            additionRevenue: 0,
            totalRevenue: 0,
            laborCost: 0,
            laborCostRatio: 0,
            materialCost: 0,
            materialCostRatio: 0,
            equipmentCost: 0,
            equipmentCostRatio: 0,
            subcontractCost: 0,
            subcontractCostRatio: 0,
            totalCost: 0,
            balance: 0,
            recordCount: 0,
          };
          const totalCostPct = totalAllCost > 0 ? Number(((stat.totalCost / totalAllCost) * 100).toFixed(2)) : 0;
          const isEmptyStat = stat.recordCount === 0
            && stat.totalCost === 0
            && stat.totalRevenue === 0
            && stat.contractRevenue === 0
            && stat.additionRevenue === 0;

          const revenueItems: MetricItem[] = [
            { key: 'contractRevenue', label: '合同收入金额', value: stat.contractRevenue },
            { key: 'additionRevenue', label: '增项收入金额', value: stat.additionRevenue },
            { key: 'totalRevenue', label: '总收入', value: stat.totalRevenue },
          ].filter((item) => Number(item.value || 0) !== 0);

          const costItems: MetricItem[] = [
            { key: 'laborCost', label: '人工成本', value: stat.laborCost, ratio: stat.laborCostRatio, showProgress: true },
            { key: 'equipmentCost', label: '设备成本', value: stat.equipmentCost, ratio: stat.equipmentCostRatio, showProgress: true },
            { key: 'materialCost', label: '材料成本', value: stat.materialCost, ratio: stat.materialCostRatio, showProgress: true },
            { key: 'subcontractCost', label: '分包成本', value: stat.subcontractCost, ratio: stat.subcontractCostRatio, showProgress: true },
          ].filter((item) => Number(item.value || 0) !== 0);

          const showBalance = Number(stat.balance || 0) !== 0;

          return (
            <Col xs={24} sm={12} md={12} key={cfg.type}>
              <Card
                size="small"
                title={
                  <span>
                    <Tag color={cfg.tagColor} style={{ marginRight: 6, borderRadius: 6 }}>{cfg.type}</Tag>
                    {cfg.label}
                  </span>
                }
                style={{ borderRadius: 16, border: `1px solid ${cfg.border}`, background: cfg.bg, height: '100%' }}
                styles={{ body: { padding: isMobile ? 12 : 16 } }}
              >
                <div style={{ marginBottom: isEmptyStat ? 0 : 14 }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: showBalance ? (isMobile ? '1fr' : '1.2fr 0.8fr') : '1fr',
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        padding: isMobile ? '12px 14px' : '14px 16px',
                        borderRadius: 14,
                        background: 'rgba(255,255,255,0.46)',
                        border: `1px solid ${cfg.border}`,
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
                      }}
                    >
                      <div style={{ color: '#667986', fontSize: 12, marginBottom: 6 }}>
                        当前分类总成本
                      </div>
                      <Text style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: cfg.color }}>
                        {formatMoney(stat.totalCost)}
                      </Text>
                    </div>
                    {showBalance ? (
                      <div
                        style={{
                          padding: isMobile ? '12px 14px' : '14px 16px',
                          borderRadius: 14,
                          background: stat.balance >= 0 ? 'rgba(82, 196, 26, 0.10)' : 'rgba(245, 34, 45, 0.08)',
                          border: stat.balance >= 0 ? '1px solid rgba(82, 196, 26, 0.22)' : '1px solid rgba(245, 34, 45, 0.18)',
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
                        }}
                      >
                        <div style={{ color: '#667986', fontSize: 12, marginBottom: 6 }}>
                          当前结余
                        </div>
                        <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, color: stat.balance >= 0 ? '#389e0d' : '#cf1322' }}>
                          {formatMoney(stat.balance)}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <Progress
                    percent={totalCostPct}
                    size="small"
                    strokeColor={cfg.color}
                    trailColor="rgba(16,27,35,0.08)"
                    style={{ marginTop: 10, marginBottom: 0 }}
                  />
                </div>

                {isEmptyStat ? (
                  <div style={{ marginTop: 12, color: '#8c9aa6', fontSize: 12, lineHeight: 1.7 }}>
                    当前分类暂无收入与成本数据
                  </div>
                ) : (
                  <>
                    <MetricSection title="收入" items={revenueItems} progressColor={cfg.color} variant="income" />
                    <MetricSection title="成本" items={costItems} progressColor={cfg.color} variant="cost" />

                    <div style={{ marginTop: 10, color: '#667986', fontSize: 11 }}>
                      共 {stat.recordCount} 条记录
                    </div>
                  </>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default OperatingInsightsPanel;
